import React, { useEffect, useState } from 'react';
import { Button, Collapse, message, Modal, Progress, Skeleton, Spin, Table, Tag } from 'antd';
import './CompareProductsModal.scss';
import type { ButtonProps } from 'antd/lib/button/button';
import { useProductIQApi } from 'api/product_iq';
import { Product, ProductComparisonData } from 'models/SupplierDynamics';
import { ExportOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useRenderedHtml } from 'hooks';
import { useUtilsApi } from 'api/useUtilsApi';
import fileDownload from 'js-file-download';

interface CompareProductsModalProps {
  products: Product[];
  vendorDomain: string;
  title?: string | React.ReactElement;
  open?: boolean;
  cancelText?: string;
  okText?: string;
  onCancel?: () => void;
  onOk?: () => void;
  cancelButtonProps?: ButtonProps;
}
const CompareProductsModal = ({
  products,
  title,
  open,
  okText,
  cancelText,
  onCancel,
  onOk,
  cancelButtonProps
}: CompareProductsModalProps) => {
  const { ref, html } = useRenderedHtml<HTMLDivElement>();
  const { htmlToPdf } = useUtilsApi();
  const [isExporting, setIsExporting] = useState(false);

  const [comparisonData, setComparisonData] = useState<ProductComparisonData[]>([]);
  const [loadedCount, setLoadedCount] = useState<number>(0);

  const { compareProducts } = useProductIQApi();

  useEffect(() => {
    const fetchComparisonData = async () => {
      try {
        setComparisonData([]); // Reset comparison data when starting
        setLoadedCount(0); // Reset loaded count when starting

        // Initiate all calls concurrently
        const promises = products.map((product) => {
          return compareProducts(product.name, product.vendor_domain)
            .then((response) => {
              // Update the state for the specific product when it resolves
              const result = {
                ...response,
                product: product
              };
              setComparisonData((prev) => [...prev, result]);
              // Increment loaded count when a product is successfully loaded
              setLoadedCount((prev) => prev + 1);
            })
            .catch((error) => {
              // Handle errors and log them, but don't update state with error
              console.error(`Error fetching comparison data for product ${product.name}:`, error);
              // Increment loaded count even on error to track progress
              setLoadedCount((prev) => prev + 1);
            });
        });

        // Use Promise.allSettled only to know when the entire group of requests is complete,
        // but the UI updates happen within the individual .then/.catch blocks above
        await Promise.allSettled(promises);
      } catch (error) {
        console.error('Error fetching comparison data:', error);
      }
    };
    if (open) {
      products?.length > 0 && fetchComparisonData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  /**
   * Gets the score color based on rating
   * @param {string} rating - The rating string (e.g., "8/10")
   * @returns {string} The color class for the score badge
   */
  const getScoreColor = (rating: string): string => {
    const score = parseFloat(rating.split('/')[0]);
    if (score >= 8) return 'green';
    if (score >= 6) return 'orange';
    return 'red';
  };

  const handlePdfClick = async (): Promise<void> => {
    if (!html) {
      message.warning('No content available to export');
      return;
    }

    setIsExporting(true);
    try {
      const reportType = 'Product Comparison';
      // Create filename from all product names
      const productNames = comparisonData.map((p) => p.title).join('-');
      const filename = `${productNames}.pdf`;
      const pdfBlob = await htmlToPdf(html, 'Product Comparison', reportType);
      fileDownload(pdfBlob, filename);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      message.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Creates table columns based on vendors
   * @param {ProductComparisonData} product - The product comparison data containing vendors
   * @param {boolean} isExport - Whether the columns are for export (removes fixed positioning)
   * @returns {any[]} Array of table column configurations
   */
  const createColumns = (product: ProductComparisonData, isExport = false) => {
    if (!product?.vendors) return [];

    const baseColumns = [
      {
        title: 'Features',
        dataIndex: 'featureTitle',
        key: 'featureTitle',
        width: 200,
        ...(isExport ? {} : { fixed: 'left' as const }),
        render: (text: string, record: any) => (
          <div>
            <div className="font-semibold text-gray-900 mb-1">{text}</div>
            <div className="text-xs">{record.description}</div>
          </div>
        )
      }
    ];

    const vendorColumns = product.vendors.map((vendor) => ({
      title: (
        <div className="text-center">
          <div className="font-semibold text-gray-900 mb-1">{vendor.name}</div>
          <div className="text-xs text-gray-500">{vendor.product}</div>
        </div>
      ),
      dataIndex: vendor.id,
      key: vendor.id,
      width: 250,
      align: 'left' as const,
      render: (scores: any, _record: any) => {
        const vendorScore = scores?.find((score: any) => score.vendor === vendor.id);
        if (!vendorScore || (vendorScore.details === 'N/A' && vendorScore.limitation === 'N/A'))
          return <div className="text-gray-400 text-center">N/A</div>;

        return (
          <div className="vendor-score-cell">
            {/* Score Badge */}
            <div className="score-section mb-2">
              <Tag color={getScoreColor(vendorScore.rating)} className="text-sm font-semibold">
                {vendorScore.rating}
              </Tag>
            </div>

            {/* Details */}
            <div className="details-section mb-2">
              <div className="text-xs font-bold">
                {vendorScore.details && vendorScore.details !== 'N/A' ? (
                  vendorScore?.details
                ) : (
                  <span className="text-gray-400">-na-</span>
                )}
              </div>
            </div>

            {/* Limitation */}
            <div className="limitation-section">
              <div className="text-xs text-red-600">
                Limitation:{' '}
                {vendorScore.limitation && vendorScore.limitation !== 'N/A' ? (
                  vendorScore?.limitation
                ) : (
                  <span className="text-gray-400">-na-</span>
                )}
              </div>
            </div>
          </div>
        );
      }
    }));

    return [...baseColumns, ...vendorColumns];
  };

  /**
   * Creates data source from features
   * @param {ProductComparisonData} product - The product comparison data containing features
   * @returns {any[]} Array of data objects for the table
   */
  const createDataSource = (product: ProductComparisonData) => {
    if (!product?.features) return [];

    return product.features.map((feature) => ({
      key: feature.id,
      featureTitle: feature.title,
      description: feature.description,
      ...product.vendors?.reduce((acc, vendor) => {
        acc[vendor.id] = feature.scores;
        return acc;
      }, {} as any)
    }));
  };

  const handleCancel = () => {
    if (loadedCount < products.length) {
      message.warning('Please wait for the comparison to complete');
      return;
    }
    onCancel && onCancel();
  };

  /**
   * Calculates the loading progress percentage
   * @returns {number} Progress percentage (0-100)
   */
  const getProgressPercentage = (): number => {
    if (!products?.length) return 0;
    return Math.round((loadedCount / products.length) * 100);
  };

  /**
   * Creates a custom title with progress bar
   * @returns {React.ReactElement} Title element with progress bar (only shown while loading)
   */
  const renderTitleWithProgress = (): React.ReactElement => {
    const progressPercentage = getProgressPercentage();
    const isComplete = loadedCount === products.length && products.length > 0;

    // If loading is complete, just return the title without progress bar
    if (isComplete) {
      return <>{title}</>;
    }

    // Show progress bar while loading
    return (
      <div className="w-full">
        {title && <div className="mb-2">{title}</div>}
        <div className="flex items-center gap-2">
          <Progress
            percent={progressPercentage}
            status="active"
            showInfo={true}
            format={(_percent) => `${loadedCount}/${products.length}`}
            className="flex-1"
          />
        </div>
        <p className="text-sm text-gray-500">
          {' '}
          <Spin size="small" spinning={true} /> Searching for alternative products, this may take a minute or so...
        </p>
      </div>
    );
  };

  return (
    <Modal
      className="compare-products-table"
      title={renderTitleWithProgress()}
      open={open}
      onCancel={handleCancel}
      cancelText={cancelText}
      okText={okText}
      onOk={onOk}
      cancelButtonProps={cancelButtonProps}
      afterClose={() => {
        setComparisonData([]);
        setLoadedCount(0);
      }}
      centered
      width={'75%'}
      style={{ maxHeight: '80vh' }}
      footer={
        <div data-testid="note-footer" className="flex items-center gap-2">
          <InfoCircleOutlined className="text-xl" />
          <span className="text-sm">
            Scores are AI-generated using public data and user reviews to help guide your decision.
          </span>
        </div>
      }
    >
      <Skeleton active loading={!comparisonData.length}>
        <Collapse className="compare-products-collapse" accordion>
          {comparisonData.map((product) => (
            <Collapse.Panel
              key={product.title ? product.title : new Date().getTime().toString()}
              header={product.title ? product.title : 'Product vs Competitors'}
              showArrow={false}
            >
              {/* Summary Section */}
              {product?.summary && (
                <div className="mb-1 p-4">
                  <h3 className="font-bold">Summary/Overview</h3>
                  <p className="text-gray-700 text-sm">{product.summary}</p>
                </div>
              )}

              <Table
                bordered={true}
                size="large"
                dataSource={createDataSource(product)}
                columns={createColumns(product) as any}
                pagination={false}
                className="table-compare-products"
                scroll={{
                  x: 1200,
                  y: 500
                }}
              />
            </Collapse.Panel>
          ))}
        </Collapse>

        {comparisonData.length > 0 && (
          <div className="flex justify-end mt-4">
            <Button
              type="link"
              icon={<ExportOutlined />}
              onClick={handlePdfClick}
              loading={isExporting}
              className="!text-[#0F62FE] !h-auto flex items-center gap-1 font-medium"
            >
              Export to PDF
            </Button>
          </div>
        )}

        {/* Hidden container for PDF export */}
        <div style={{ position: 'absolute', top: '-9999px', left: '-9999px' }} ref={ref}>
          <style>{`
          @page { size: landscape; margin: 10px; }

          /* --- Page structure --- */
          .pdf-page {
            padding: 2px 10px;
          }
          .pdf-page + .pdf-page {
            page-break-before: always;
          }

          /* --- Product title --- */
          .pdf-page h2 {
            font-size: 13px !important;
            font-weight: bold;
            margin-top: 0 !important;
            margin-bottom: 4px !important;
            padding-bottom: 0 !important;
            border-bottom: none !important;
            color: #333;
          }

          /* --- Summary block --- */
          .pdf-page .summary-section {
            font-size: 9px !important;
            margin-bottom: 6px !important;
            padding: 4px 8px !important;
            background-color: #f5f5f5;
            border-left: 3px solid #0F62FE;
          }
          .pdf-page .summary-section h3 {
            font-size: 9px !important;
            font-weight: bold;
            margin-top: 0 !important;
            margin-bottom: 2px !important;
            color: #0F62FE;
          }
          .pdf-page .summary-section p {
            margin: 0;
            line-height: 1.3 !important;
            color: #555;
          }

          /* --- Table layout --- */
          .pdf-page table {
            font-size: 8px !important;
            width: 100%;
            margin: 2px 0 !important;
            page-break-inside: auto !important;
          }
          .pdf-page tr {
            page-break-inside: avoid !important;
          }
          .pdf-page th {
            padding: 2px 4px !important;
            font-size: 9px !important;
            font-weight: 600 !important;
            background-color: #fafafa !important;
            color: #333 !important;
            line-height: 1.2 !important;
          }
          .pdf-page td {
            padding: 2px 4px !important;
            font-size: 8px !important;
            line-height: 1.1 !important;
          }

          /* --- Score badge --- */
          .pdf-page .ant-tag {
            font-size: 7px !important;
            padding: 1px 4px !important;
            line-height: 1.3 !important;
            margin: 0 !important;
            border-radius: 2px !important;
            font-weight: 600 !important;
            display: inline-block !important;
          }
          .pdf-page .ant-tag-green {
            background-color: #f6ffed !important;
            border: 1px solid #b7eb8f !important;
            color: #52c41a !important;
          }
          .pdf-page .ant-tag-orange {
            background-color: #fff7e6 !important;
            border: 1px solid #ffd591 !important;
            color: #fa8c16 !important;
          }
          .pdf-page .ant-tag-red {
            background-color: #fff2f0 !important;
            border: 1px solid #ffccc7 !important;
            color: #ff4d4f !important;
          }

          /* --- Vendor score cell spacing --- */
          .pdf-page .vendor-score-cell > div {
            margin-bottom: 2px !important;
          }

          /* --- Limitation text in red --- */
          .pdf-page .limitation-section {
            color: #dc2626 !important;
          }
          .pdf-page .limitation-section div {
            color: #dc2626 !important;
          }

          /* --- Feature title and description --- */
          .pdf-page .font-semibold {
            font-weight: 600 !important;
            color: #000 !important;
          }
          .pdf-page .text-xs {
            font-size: 7px !important;
            line-height: 1.1 !important;
            color: #2c2e31 !important;
          }
          .pdf-page .text-gray-400 {
            color: #9ca3af !important;
          }

          /* --- Backend-injected supplier header and divider --- */
          .supplier-header {
            margin-bottom: 2px !important;
            font-size: 9pt !important;
          }
          .header-divider {
            margin: 2px 0 4px 0 !important;
          }

          /* --- Disclaimer flows on page 1 after the table --- */
          .npi-disclaimer {
            margin-top: 6px !important;
            padding-top: 6px !important;
          }
          .npi-disclaimer h3 {
            font-size: 9pt !important;
            margin-bottom: 2px !important;
          }
          .disclaimer-text {
            font-size: 8pt !important;
            line-height: 1.3 !important;
          }
        `}</style>

          {/* Comparison Pages */}
          {comparisonData.map((product, index) => (
            <div key={product.title || 'unknown'} id={`page-${index}`} className="pdf-page">
              <h2>{product.title || 'Product vs Competitors'}</h2>
              {product?.summary && (
                <div className="summary-section">
                  <h3>Summary/Overview</h3>
                  <p>{product.summary}</p>
                </div>
              )}
              <Table
                bordered={true}
                size="small"
                dataSource={createDataSource(product)}
                columns={createColumns(product, true)}
                pagination={false}
              />
            </div>
          ))}
        </div>
      </Skeleton>
    </Modal>
  );
};
export default CompareProductsModal;
